import { useState, useEffect, useCallback } from "react";

interface UseResizableSidebarOptions {
    /** Initial width in pixels. @default 420 */
    initialWidth?: number;
    /** Minimum width in pixels. @default 300 */
    minWidth?: number;
    /** Maximum width as a fraction of window width. @default 0.8 */
    maxWidthFraction?: number;
}

/**
 * Custom hook for drag-resizable sidebar panels.
 * Returns the current width, dragging state, and a mousedown handler
 * that should be attached to the resize handle element.
 */
export function useResizableSidebar({
    initialWidth = 420,
    minWidth = 300,
    maxWidthFraction = 0.8,
}: UseResizableSidebarOptions = {}) {
    const [width, setWidth] = useState(initialWidth);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth > minWidth && newWidth < window.innerWidth * maxWidthFraction) {
                setWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.body.style.userSelect = "none";
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.body.style.userSelect = "";
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, minWidth, maxWidthFraction]);

    const startDragging = useCallback(() => setIsDragging(true), []);

    return { width, isDragging, startDragging } as const;
}
